import { Building2, MapPin, DollarSign, Calendar, ExternalLink, AlertTriangle, ChevronDown } from "lucide-react";
import { format, isValid, parseISO } from "date-fns";
import { useState, useRef, useEffect } from "react";

const platformColors = {
  LinkedIn: "#0077B5",
  Naukri: "#0095FF",
  Indeed: "#2557A7",
  Glassdoor: "#0CAA41",
  Monster: "#6E46AE",
  ZipRecruiter: "#58A94A",
  Other: "#94A3B8",
  manual: "#A3C3D8"
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = parseISO(dateString);
    if (isValid(date)) {
      return format(date, 'MMM d');
    }
    return 'N/A';
  } catch {
    return 'N/A';
  }
};

// Monster link dropdown component
const MonsterLinkDropdown = ({ job, onStopPropagation }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = (e) => {
    e.stopPropagation();
    setIsOpen(!isOpen);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleToggle}
        className="flex items-center gap-0.5 text-[#94A3B8] hover:text-[#6E46AE] transition-colors"
        title="Monster links (search-based)"
      >
        <ExternalLink className="h-3.5 w-3.5" />
        <ChevronDown className="h-2.5 w-2.5" />
      </button>
      
      {isOpen && (
        <div className="absolute right-0 bottom-6 z-50 bg-white border border-slate-200 rounded-lg shadow-lg py-1 min-w-[180px]">
          <div className="px-3 py-1.5 border-b border-slate-100">
            <div className="flex items-center gap-1 text-[10px] text-amber-600">
              <AlertTriangle className="h-3 w-3" />
              <span>Links may show search results</span>
            </div>
          </div>
          <a
            href={job.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => {
              e.stopPropagation();
              setIsOpen(false);
            }}
            className="flex items-center gap-2 px-3 py-2 text-xs text-[#555F67] hover:bg-slate-50 transition-colors"
          >
            <ExternalLink className="h-3 w-3" />
            <span>Search on Monster</span>
          </a>
          {job.original_url && job.original_url !== job.url && (
            <a
              href={job.original_url}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => {
                e.stopPropagation();
                setIsOpen(false);
              }}
              className="flex items-center gap-2 px-3 py-2 text-xs text-[#555F67] hover:bg-slate-50 transition-colors"
            >
              <ExternalLink className="h-3 w-3" />
              <span>Original Page</span>
              <span className="text-[10px] text-emerald-600">(if active)</span>
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export const JobCard = ({ application, isDragging }) => {
  const { job } = application;
  
  if (!job) return null;

  const platformColor = platformColors[job.platform] || platformColors.Other;
  const isMonster = job.platform === 'Monster';

  return (
    <div 
      className={`bg-white border border-slate-100 rounded-lg p-4 cursor-pointer transition-all group ${
        isDragging 
          ? 'shadow-lg border-[#A3C3D8] rotate-2 scale-105' 
          : 'shadow-sm hover:shadow-md hover:border-[#A3C3D8]/50'
      }`}
      data-testid={`job-card-${application.id}`}
    >
      {/* Header */}
      <div className="flex items-start gap-2 mb-3">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center text-white text-sm font-semibold flex-shrink-0"
          style={{ backgroundColor: platformColor }}
        >
          {job.company.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1 min-w-0 overflow-hidden">
          <h4 
            className="font-medium text-[#2D3748] text-sm truncate group-hover:text-[#4A728D] transition-colors"
            style={{ fontFamily: 'Manrope, sans-serif' }}
            title={job.title}
          >
            {job.title}
          </h4>
          <p className="text-xs text-[#94A3B8] truncate flex items-center gap-1">
            <Building2 className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{job.company}</span>
          </p>
        </div>
      </div>

      {/* Details */}
      <div className="space-y-1.5 mb-3">
        {job.location && (
          <div className="flex items-center gap-1.5 text-xs text-[#555F67]">
            <MapPin className="h-3 w-3 text-[#94A3B8]" />
            <span className="truncate">{job.location}</span>
          </div>
        )}
        {job.salary && (
          <div className="flex items-center gap-1.5 text-xs text-[#555F67]">
            <DollarSign className="h-3 w-3 text-[#94A3B8]" />
            <span className="truncate">{job.salary}</span>
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <div className="flex items-center gap-1.5 text-xs text-[#94A3B8]">
          <Calendar className="h-3 w-3" />
          <span>{formatDate(application.created_at)}</span>
        </div>
        <div className="flex items-center gap-2">
          {/* Monster badge with warning indicator */}
          {isMonster ? (
            <span 
              className="text-xs px-2 py-0.5 rounded-full flex items-center gap-1"
              style={{ 
                backgroundColor: `${platformColor}15`,
                color: platformColor
              }}
              title="Monster links are search-based approximations"
            >
              {job.platform}
              <AlertTriangle className="h-2.5 w-2.5 text-amber-500" />
            </span>
          ) : (
            <span 
              className="text-xs px-2 py-0.5 rounded-full"
              style={{ 
                backgroundColor: `${platformColor}15`,
                color: platformColor
              }}
            >
              {job.platform}
            </span>
          )}
          
          {/* External link - different handling for Monster */}
          {job.url && !job.url.startsWith('manual://') && (
            isMonster ? (
              <MonsterLinkDropdown job={job} />
            ) : (
              <a
                href={job.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-[#94A3B8] hover:text-[#4A728D] transition-colors"
              >
                <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default JobCard;
